import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import type { CreateIntercompanyTransactionInput } from '@accounting/shared';
import { useCreateIntercompany, useIntercompanyTransactions, useVoidIntercompany } from '../../hooks/useIntercompany';
import { useCompanies } from '../../hooks/useCompanies';
import { useAccounts } from '../../hooks/useAccounts';
import { AccountPicker } from '../../components/accounts/AccountPicker';
import { ApiError } from '../../lib/api';
import { formatAccountingDate, formatCurrency, todayIso } from '../../lib/formatters';

export const Route = createFileRoute('/_auth/intercompany/')({
  component: IntercompanyPage,
});

function IntercompanyPage() {
  const { data: companies = [] } = useCompanies();
  const { data: txns = [], isLoading } = useIntercompanyTransactions();
  const create = useCreateIntercompany();
  const voidTxn = useVoidIntercompany();

  const [open, setOpen] = useState(false);

  const activeCompanies = companies.filter((c) => c.isActive);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 24, margin: 0 }}>Intercompany</h1>
          <p className="muted" style={{ margin: '4px 0 0', fontSize: 13 }}>
            Due To / Due From transfers between companies — eliminated in consolidation
          </p>
        </div>
        <button className="btn-primary" onClick={() => setOpen((v) => !v)} disabled={activeCompanies.length < 2}>
          {open ? 'Close' : '+ New transaction'}
        </button>
      </div>

      {activeCompanies.length < 2 && (
        <div className="card" style={{ padding: 16 }}>
          <p className="muted" style={{ margin: 0 }}>You need at least two active companies to record an intercompany transaction.</p>
        </div>
      )}

      {open && <NewIntercompanyForm companies={activeCompanies} create={create} onDone={() => setOpen(false)} />}

      {isLoading && <p className="muted">Loading…</p>}
      {!isLoading && txns.length === 0 && (
        <div className="card" style={{ textAlign: 'center', padding: 32 }}>
          <p className="muted" style={{ margin: 0 }}>No intercompany transactions yet.</p>
        </div>
      )}

      {txns.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>Date</th>
              <th>From (funder)</th>
              <th>To (beneficiary)</th>
              <th>Description</th>
              <th style={{ textAlign: 'right' }}>Amount</th>
              <th style={{ width: 1 }}></th>
            </tr>
          </thead>
          <tbody>
            {txns.map((t) => (
              <tr key={t.id} style={{ opacity: t.status === 'voided' ? 0.5 : 1 }}>
                <td>{formatAccountingDate(t.date)}</td>
                <td>{t.fromCompanyName}</td>
                <td>{t.toCompanyName}</td>
                <td>{t.description ?? '—'}</td>
                <td style={{ textAlign: 'right' }}>{formatCurrency(t.amount)}</td>
                <td>
                  {t.status === 'voided' ? (
                    <span className="tag bad">voided</span>
                  ) : (
                    <button
                      className="btn-secondary"
                      onClick={() => {
                        const reason = prompt('Reason for voiding? (reverses both companies)');
                        if (reason && reason.trim()) voidTxn.mutate({ id: t.id, reason: reason.trim() });
                      }}
                    >
                      Void
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function NewIntercompanyForm({
  companies,
  create,
  onDone,
}: {
  companies: { id: string; name: string }[];
  create: ReturnType<typeof useCreateIntercompany>;
  onDone: () => void;
}) {
  const [fromCompanyId, setFromCompanyId] = useState('');
  const [toCompanyId, setToCompanyId] = useState('');
  const [date, setDate] = useState(todayIso());
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [fromAccountId, setFromAccountId] = useState('');
  const [toAccountId, setToAccountId] = useState('');

  const { data: fromAccounts = [] } = useAccounts(fromCompanyId || null);
  const { data: toAccounts = [] } = useAccounts(toCompanyId || null);
  const isPostable = (a: typeof fromAccounts[number]) => a.isActive && a.allowDirectPosting && !a.isIntercompany;

  const errorMessage =
    create.error instanceof ApiError
      ? (create.error.body as { error?: string } | null)?.error ?? 'Could not record transaction'
      : create.error?.message;

  const valid =
    fromCompanyId && toCompanyId && fromCompanyId !== toCompanyId &&
    fromAccountId && toAccountId && parseFloat(amount) > 0;

  const submit = () => {
    const input: CreateIntercompanyTransactionInput = {
      fromCompanyId, toCompanyId, date,
      amount: String(parseFloat(amount) || 0),
      description: description.trim() || undefined,
      fromAccountId, toAccountId,
    };
    create.mutate(input, { onSuccess: () => onDone() });
  };

  const acctSelect = (val: string, set: (v: string) => void, list: typeof fromAccounts, disabled: boolean) => (
    <AccountPicker
      accounts={list}
      value={val}
      onChange={set}
      predicate={isPostable}
      disabled={disabled}
      placeholder="— account —"
    />
  );

  return (
    <div className="card" style={{ padding: 16, marginBottom: 16, maxWidth: 760 }}>
      <h2 style={{ fontSize: 16, marginTop: 0 }}>New intercompany transaction</h2>
      <div className="form-grid">
        <label>From company (funder)</label>
        <select value={fromCompanyId} onChange={(e) => { setFromCompanyId(e.target.value); setFromAccountId(''); }}>
          <option value="">— select —</option>
          {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>

        <label>Credited account (its cash)</label>
        {acctSelect(fromAccountId, setFromAccountId, fromAccounts, !fromCompanyId)}

        <label>To company (beneficiary)</label>
        <select value={toCompanyId} onChange={(e) => { setToCompanyId(e.target.value); setToAccountId(''); }}>
          <option value="">— select —</option>
          {companies.filter((c) => c.id !== fromCompanyId).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>

        <label>Debited account (expense or cash)</label>
        {acctSelect(toAccountId, setToAccountId, toAccounts, !toCompanyId)}

        <label htmlFor="ic-date">Date</label>
        <input id="ic-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} style={{ maxWidth: 200 }} />

        <label htmlFor="ic-amt">Amount</label>
        <input id="ic-amt" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" style={{ maxWidth: 180, textAlign: 'right' }} />

        <label htmlFor="ic-desc">Description</label>
        <input id="ic-desc" value={description} onChange={(e) => setDescription(e.target.value)} maxLength={500} />
      </div>

      <p className="muted" style={{ fontSize: 12 }}>
        Posts two balanced entries: the funder books an Intercompany Receivable (Due From); the
        beneficiary books an Intercompany Payable (Due To). Both net to zero on consolidated reports.
      </p>

      {errorMessage && <div className="field-error" style={{ marginTop: 8 }}>{errorMessage}</div>}
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button className="btn-primary" onClick={submit} disabled={!valid || create.isPending}>
          {create.isPending ? 'Posting…' : 'Post transaction'}
        </button>
        <button className="btn-secondary" onClick={onDone}>Cancel</button>
      </div>
    </div>
  );
}
